import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CustomButton from "../../components/UI/Buttons/CustomButton";
import Warning from "../../components/UI/Icons/Warning";
import CheckboxInput from "../../components/UI/Inputs/Checkbox";
import PasswordTooltip from "../../components/UI/Inputs/PasswordTooltip";
import TextInput from "../../components/UI/Inputs/TextInput";
import ErrorMessage from "../../components/UI/Tooltips/ErrorMessage";
import {
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
  emailError
} from "../../constants/app";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../store/application/slice";
import {
  resetAuthErrorAction,
  setShowSignUpDetails,
} from "../../store/auth/slice";
import {
  checkEmailValidationThunk,
  getAddressSuggestionsThunk,
  signUpThunk,
} from "../../store/auth/thunks";
import {
  AddressSuggestion,
  TEmailValidation,
} from "../../store/auth/types";
import { createValidationSchema } from "../../utils/createValidationSchema";
import { capitalizeName } from "../../utils/stringUtils";
import { NON_AUTH_ROUTE } from "../App/routes";
import "./Signup.scss";

/**
 * Signup Page Props Interface
 * @interface SignupPageProps
 */
interface SignupPageProps {}

const SignupPage: FC<SignupPageProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const error = useSelector((state) => state.auth.error);
  const inProgress = useSelector((state) => state.auth.inProgress);
  const createAccinProgress = useSelector((state) => state.auth.createAccinProgress);
  const showSignUpDetails = useSelector(
    (state) => state.auth.showSignUpDetails
  );
  // const addressSuggestionArr = useSelector(
  //   (state) => state.auth.addressSuggestions
  // );

  const [isHaveAustralianWorkingRights, setIsHaveAustralianWorkingRights] =
    useState<boolean>(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [hasAddressSelected, setHasAddressSelected] = useState<boolean>(false);

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (debouncedSearch.length === 0) {
      setSelectedKeyword(debouncedSearch);
      setAddressSuggestions([]);
    } else if (debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH) {
      setSelectedKeyword(debouncedSearch);
      handleAddressSuggestions(debouncedSearch);
    }
  }, [debouncedSearch]);

  const handleAddressSuggestions = async (selectedKeyword: string) => {
    const response = await dispatch(
      getAddressSuggestionsThunk(selectedKeyword)
    );

    if (isFulfilled(response)) {
      setAddressSuggestions(response.payload.data);
    } else {
      setAddressSuggestions([]);
    }
  };

  const toggleIsAustralianWorkingRights = () => {
    setIsHaveAustralianWorkingRights(!isHaveAustralianWorkingRights);
  };

  const handleCreateAccount = async () => {
    const payload: TEmailValidation = {
      email: values.email,
      referral_code: values.referral_code,
    };
    const result = await dispatch(checkEmailValidationThunk(payload));
    if (isFulfilled(result) && Object.entries(errors).length === 0) {      
      const signUpPayload = {
        ...values,
        first_name: capitalizeName(values.first_name),
        last_name: capitalizeName(values.last_name),
      };
      dispatch(showTransparentPreloaderAction())
      const result = await dispatch(signUpThunk(signUpPayload));
      if(isFulfilled(result)) {
        localStorage.setItem("verified_email", values.email);
        dispatch(showNotificationAction("Verification email sent. Please check your inbox."));
        dispatch(setShowSignUpDetails(true));
      }
      dispatch(hideTransparentPreloaderAction())
    }
  };



  const fname = sessionStorage.getItem("first_name");
  const lname = sessionStorage.getItem("last_name");
  const addr = sessionStorage.getItem("address");
  const mail_id = sessionStorage.getItem("signup_email");
  const pass = sessionStorage.getItem("signup_password");
  const ref_code = sessionStorage.getItem("referral_code");

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    setHasAddressSelected(false);
    setFieldValue("address", inputValue);
    setSearchInput(inputValue);
  };

  const handleSelectAddress = (selectedAddress: string) => {
    setFieldValue("address", selectedAddress);
    setAddressSuggestions([]); // Hide dropdown after selection
    setSelectedKeyword("");
    setHasAddressSelected(true);
  };

  const {
    handleSubmit,
    errors,
    values,
    touched,
    setFieldValue,
    handleChange,
    resetForm,
  } = useFormik({
    validationSchema: createValidationSchema("signup"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      first_name: fname ? capitalizeName(fname) : "",
      last_name: lname ? capitalizeName(lname) : "",
      email: mail_id ? mail_id : "",
      password: pass ? pass : "",
      role_id: 5,
      working_rights_in_australia: false,
      address: addr ? addr : "",
      referral_code: ref_code ? ref_code : "",
    },
    onSubmit: handleCreateAccount,
  });

  useEffect(() => {
    setFieldValue("working_rights_in_australia", isHaveAustralianWorkingRights);
  }, [setFieldValue, isHaveAustralianWorkingRights]);

  useEffect(() => {
    dispatch(resetAuthErrorAction());

    sessionStorage.setItem("signup_email", values.email);
  }, [dispatch, values]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referral_code = urlParams.get("ref");
    setFieldValue("referral_code", referral_code);
  }, [setFieldValue]);

  const handleCreateEnquiry = () => {
    navigate(NON_AUTH_ROUTE.CREATE_ENQUIRY);
  };

  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const passwordFieldRef = useRef(null);
  const handlePasswordFocus = () => setShowPasswordTooltip(true);
  const handlePasswordBlur = () => setShowPasswordTooltip(false);

    useEffect(() => {
    if (showSignUpDetails) {
      resetForm();
      setIsHaveAustralianWorkingRights(false);
      setFieldValue("first_name", "");
      setFieldValue("last_name", "");
      setFieldValue("email", "");
      setFieldValue("password", "");
      setFieldValue("address", "");
      setFieldValue("referral_code", "");
      sessionStorage.clear();
    }
  }, [showSignUpDetails, resetForm]);
  
  return (
    <>
          <div className="fs-22 fw-400 text-black" data-testid="signup">
            Candidate Sign Up
          </div>
          {/* <div className="d-none d-lg-block fs-16 fw-400 text-black mb-0">
            Please be prepared to upload items such as qualifications and
            certificates, an online interview, a police check and other key
            documents.
          </div> */}
          <form
            className="d-flex flex-column gap-20"
            onSubmit={handleSubmit} // Binds Formik's submit handler to the form
          >
            <div className="d-flex gap-10">
              <TextInput
                value={values.first_name || ""}
                field={"first_name"}
                label={"First Name"}
                onChange={handleChange}
                touched={touched.first_name!}
                error={errors.first_name}
                placeholder={" " + ""}
              />
              <TextInput
                value={values.last_name || ""}
                field={"last_name"}
                label={"Last Name"}
                onChange={handleChange}
                touched={touched.last_name!}
                error={errors.last_name}
                placeholder={" " + ""}
              />
            </div>
            <div className="mt-10 position-relative">
              <TextInput
                value={values.address || ""}
                field={"address"}
                label={"Home Address"}
                onChange={handleAddressChange}
                touched={touched.address!}
                error={errors.address}
                placeholder={" " + ""}
              />
              {/* Address Suggestions Dropdown */}
              {inProgress ? (
                ""
              ) : addressSuggestions.length > 0 ? (
                <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
                  {addressSuggestions.map((addr, index) => (
                    <div
                      key={index}
                      className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                      onClick={() => handleSelectAddress(addr.description)}
                    >
                      {addr.description}
                    </div>
                  ))}
                </div>
              ) : selectedKeyword.length > 0 &&
                addressSuggestions.length === 0 ? (
                <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">
                  No Address Found
                </div>
              ) : (
                ""
              )}
              {error && <ErrorMessage message={error?.errors?.address} />}
            </div>
            <div className="mt-10">
              <TextInput
                value={values.email || ""}
                field={"email"}
                label={"Email"}
                onChange={handleChange}
                touched={touched.email!}
                error={errors.email}
                placeholder={" " + ""}
              />
              {error && error?.errors?.email?.[0] === emailError ? (
                <div className="mt-1 d-flex gap-8 align-items-center">
                  <Warning width={"18"} height={"18"} color={"#e52514"} />{" "}
                  <div className="text-alert fs-13 fw-400">
                    This email is already registered.{" "}
                    <Link to={NON_AUTH_ROUTE.LOGIN} className="text-alert">
                      Try to login
                    </Link>{" "}
                    or{" "}
                    <Link
                      to={NON_AUTH_ROUTE.FORGOT_PASSWORD}
                      className="text-alert"
                    >
                      reset your password
                    </Link>
                    .
                  </div>
                </div>
              ) : (
                <ErrorMessage message={error?.errors?.email?.[0]} />
              )}
            </div>
            <div className="mt-10 position-relative">
              <TextInput
                value={values.password || ""}
                type={"password"}
                field={"password"}
                label={"Password"}
                onChange={handleChange}
                touched={touched.password!}
                error={errors.password}
                placeholder={" " + ""}
                ref={passwordFieldRef}
                onFocus={handlePasswordFocus}
                onBlur={handlePasswordBlur}
              />
              {showPasswordTooltip && <PasswordTooltip />}
              {error && <ErrorMessage message={error?.errors?.password} />}
            </div>
            <div className="mt-10">
              <TextInput
                value={values.referral_code || ""}
                field={"referral_code"}
                label={"Referral Code"}
                defaultLabel={"Got a referral code?"}
                onChange={handleChange}
                touched={touched.referral_code!}
                error={errors.referral_code}
                placeholder={" " + ""}
              />
              {error && (
                <ErrorMessage message={error?.errors?.referral_code?.[0]} />
              )}
            </div>
            <div className="mt-10">
              <div className="theme-check">
                <CheckboxInput
                  checked={isHaveAustralianWorkingRights}
                  label="I have working rights in Australia."
                  onChange={toggleIsAustralianWorkingRights}
                  id={"working_rights_in_australia"}
                  themeCheck={true}
                />
              </div>
              {errors && (
                <ErrorMessage
                  message={errors.working_rights_in_australia as string}
                />
              )}
            </div>
            <div className="d-flex flex-column gap-20 align-items-center">
              <CustomButton
                isLoading={createAccinProgress}
                contentClass={"d-flex"}
                type="submit"
                variant="primary"
                className="fw-600 btn-theme bg-teal text-black min-w-250px mt-10"
              >
                Create Account
              </CustomButton>
              {showSignUpDetails && (
                <div className="mt-2 fs-14 fw-400 text-black text-center">
                  A verification email has been sent to{" "}
                  <strong>{localStorage.getItem("verified_email")}</strong>. Please check your inbox
                  and click the link to continue.
                </div>
              )}
              <div className="fs-13 text-grey-medium">
                Have an account already?&nbsp;
                <Link to={NON_AUTH_ROUTE.LOGIN} className="text-grey-medium">
                  Login instead
                </Link>
              </div>

              <div className="d-flex d-lg-none fs-13 text-grey-medium">
                Are you a new centre owner?&nbsp;
                <div
                  onClick={handleCreateEnquiry}
                  className="text-grey-medium text-decoration-underline"
                >
                  Submit an enquiry
                </div>
              </div>
              <div className="d-flex d-lg-none gap-10 align-items-center position-relative pt-50px">
                <div className="fs-13 fw-400 text-grey-medium">
                  &copy; {new Date().getFullYear()} Connected Crew
                </div>
                <div className="w-1px h-20px bg-grey-medium"></div>
                <Link
                  to={NON_AUTH_ROUTE.PRIVACY_POLICY}
                  className="fs-13 fw-400 text-grey-medium text-decoration-none"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>{" "}
          </form>
    </>
  );
};

export default SignupPage;
